import { useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  companyListQueryOptions,
  departmentListQueryOptions,
  designationListQueryOptions,
} from '@/features/organization/api/organization.api';
import { DataTable } from '@/components/common/DataTable';
import { AdvancedFilterPanel, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { ErrorState } from '@/components/common/ErrorState';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { userListInfiniteQueryOptions } from '../api/users.api';
import { userTypesQueryOptions } from '../api/userTypes.api';
import { useUserListFilters } from '../hooks/useUserListFilters';
import { getUserListFilterFields } from '../utils/getUserListFilterFields';
import { getUserColumns } from './UserColumns';
import { UserListHeader } from './UserListHeader';
import type { UserTypeGroup } from '../constants/user.constants';
import type { UserListItem } from '../types/user.types';

interface UserListTableProps {
  title: string;
  userTypeIds: readonly number[];
  onCreateClick?: () => void;
  category?: UserTypeGroup;
  showAllTypes?: boolean;
}

export function UserListTable({
  title,
  userTypeIds,
  onCreateClick,
  category,
  showAllTypes = false,
}: UserListTableProps) {
  const showTypeFilter = userTypeIds.length > 1;

  const {
    searchText,
    filters,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    selectedCompanyId,
    queryParams,
    handleSearchChange,
    handleFilterChange,
    applyFilterValues,
  } = useUserListFilters({ userTypeIds, showAllTypes });

  const navigate = useNavigate();

  const { data: companies } = useQuery(companyListQueryOptions());
  const { data: departments } = useQuery(
    departmentListQueryOptions(
      selectedCompanyId !== undefined ? { companyId: selectedCompanyId } : {},
    ),
  );
  const { data: designations } = useQuery(designationListQueryOptions());
  const { data: userTypes } = useQuery(userTypesQueryOptions());

  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    userListInfiniteQueryOptions(queryParams),
  );

  const viewProfileFrom = category ? `/users/${category}` : undefined;

  const openProfile = useCallback(
    (user: UserListItem) => {
      void navigate({ to: '/users/view', search: { userId: user.id, from: viewProfileFrom } });
    },
    [navigate, viewProfileFrom],
  );

  const columns = useMemo(
    () => getUserColumns(viewProfileFrom, searchText || undefined),
    [viewProfileFrom, searchText],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <UserListHeader
        title={title}
        searchText={searchText}
        onSearchChange={handleSearchChange}
        filterOpen={filterOpen}
        onFilterToggle={() => setFilterOpen((prev) => !prev)}
        activeFilterCount={activeFilterCount}
        onCreateClick={onCreateClick}
      />

      <FilterPanelCollapse isOpen={filterOpen}>
        <AdvancedFilterPanel
          fields={getUserListFilterFields({
            userTypeIds,
            showTypeFilter,
            userTypes,
            companies,
            departments,
            designations,
          })}
          values={filters}
          onChange={handleFilterChange}
          onApply={applyFilterValues}
          hasPendingChanges={hasPendingChanges}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load users"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          onRowClick={openProfile}
          stickyColumnId="fullName"
          className="lg:flex-1"
          {...loadMoreProps}
        />
      )}
    </div>
  );
}
